// src/pages/SubSubCategoryPage.jsx
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Card, Button, Typography, Spin, Empty, Tabs } from "antd";

import { useProductData } from "../hooks/useProductData";
import ImageCarousel from "../components/ImageCarousel";
import AllSubSubCategoryBoxes from "../components/AllSubSubCategoryBoxes";
import ExplorePlaces from "../components/ExplorePlaces";

import spatialImg from "../assets/images/spe-1.jpg";
import spatialTowImg from "../assets/images/spe-2.jpg";
import spatialThreeImg from "../assets/images/spe-3.jpg";
import SpatialOffers from "../components/SpatialOffers";
import FAQAccordion from "../components/FAQAccordion";

const { Paragraph, Title } = Typography;

const SubSubCategoryPage = () => {
  const [selectedSub, setSelectedSub] = useState(null);
  const { subProductSlug } = useParams();

  // Get product data and active sub-product from hook
  const { product, activeSub, loading, error } = useProductData(subProductSlug);

  const subProducts = product?.sub_products || [];

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Spin size="large" tip="Loading tours..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-20">
        <Title level={3} type="danger">
          {error}
        </Title>
        <Paragraph>Please try again later.</Paragraph>
      </div>
    );
  }

  // No sub-products
  if (!subProducts.length) {
    return (
      <div className="text-center py-20">
        <Empty description="No sub-products found" />
      </div>
    );
  }

  // Prepare carousel slides
  const slides = subProducts.map((sub) => ({
    id: sub.id,
    title: sub.title || "Untitled",
    img:
      sub.sub_product_image ||
      "https://via.placeholder.com/240x160?text=No+Image",
    rating: sub.rating,
    views: sub.views,
    banner: sub.summary || "Explore more with this package!",
    slug: `${subProductSlug}/${sub.slug}`,
  }));

  const formatChildren = (children) =>
    (children || [])
      .map((c) => {
        let t = c.text || "";
        if (c.bold) t = `<strong>${t}</strong>`;
        if (c.italic) t = `<em>${t}</em>`;
        if (c.underline) t = `<u>${t}</u>`;
        return t;
      })
      .join("");

 const renderBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return null;

  const elements = [];

  let codeBuffer = "";

  blocks.forEach((b, i) => {
    // ✅ collect table HTML
    if (b?.type === "code") {
      const text = (b.children || [])
        .map((c) => c.text || "")
        .join("");
      codeBuffer += text;
      return;
    }

    // ✅ render paragraph BEFORE table
    if (b?.type === "paragraph") {
      const text = (b.children || [])
        .map((c) => c.text || "")
        .join("")
        .trim();

      if (text) {
        elements.push(
          <p key={i} className="mb-3 text-sm">
            {text}
          </p>
        );
      }
    }

    // ✅ headings
    if (b?.type === "heading") {
      const text = (b.children || [])
        .map((c) => c.text || "")
        .join("");

      const Tag = `h${b.level || 3}`;
      elements.push(
        <Tag key={i} className="font-semibold mb-3">
          {text}
        </Tag>
      );
    }
  });

  // ✅ AFTER loop → render table
  if (codeBuffer) {
    let cleanHTML = codeBuffer
      .replace(/\n/g, "")
      .replace(/\t/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/>\s+</g, "><")
      .trim();

    cleanHTML = cleanHTML.replace(
      "<table",
      `<table border="1" style="border-collapse:collapse;width:100%;"`
    );

    elements.push(
      <div key="table" className="overflow-x-auto my-4">
        <div dangerouslySetInnerHTML={{ __html: cleanHTML }} />
      </div>
    );
  }

  return elements;
};
  return (
    <>
      {/* Carousel Section */}
      <section className="bg-lightSky border border-solid border-[#CCE1F9] pt-6 pb-6">
        <ImageCarousel
          slides={slides}
          onSelect={(selected) => {
            setSelectedSub(selected); // ✅ IMPORTANT
          }}
          initialSlideId={activeSub?.id || subProducts[0]?.id}
        />
      </section>

      {/* Sub Products Boxes */}
      <section className="py-6 px-4 lg:px-0">
        <AllSubSubCategoryBoxes
          slug={product.slug}
          subProducts={subProducts}
          activeSub={subProductSlug} // pass the full active sub-product
        />
      </section>

      <section>
        <div className="container">
          <Tabs
            className="font-medium [&_.ant-tabs-ink-bar]:bg-danger 
             [&_.ant-tabs-tab.ant-tabs-tab-active_.ant-tabs-tab-btn]:text-danger"
            items={
              activeSub?.tour_details?.map((item, i) => ({
                key: i,
                label: item.title,
                children: (
                  <div className="richText">
                    {renderBlocks(item.content || [])}
                  </div>
                ),
              })) || []
            }
          />
        </div>
      </section>

      {activeSub?.faq?.length > 0 && (
        <section className="py-6 mt-8 px-4 sm:px-0">
          <div className="container">
          <FAQAccordion faqList={activeSub?.faq} />
          </div>
        </section>
      )}

      {/* Explore Places Section */}
      <section className="py-6">
        <div className="container">
          <ExplorePlaces />
        </div>
      </section>

      {/* Offers Section */}
      <section className="py-6 px-4 lg:px-0">
        <div className="container">
          <div className="flex justify-center items-center mb-6 flex-col">
            <Title
              className="!font-medium !text-danger !text-2xl !mb-1 text-center"
              level={2}
            >
              <span className="text-primary">Offer</span> {"for you"}
            </Title>
            <Paragraph className="!mb-0 text-center sm:text-left">
              Culture, nature, thrills, and record-breaking experiences
            </Paragraph>
          </div>

          <SpatialOffers />
        </div>
      </section>
    </>
  );
};

export default SubSubCategoryPage;
